import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {AnalyticsSelectedMetricsModel} from '../../../models/api/analyticsSavedViewModel';
import {getHasAnalyticsResolutionMetrics} from '../../../store/featureFlags/featureFlagsSelectors';
import {getAnalyticsReportType} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {
  fetchAnalyticsMetrics,
  updateAnalyticsSelectedMetrics,
} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {
  getAnalyticsBucketBoundaries,
  getAnalyticsMetricsToFetch,
  getAnalyticsReportJobs,
  getAnalyticsSelectedMetrics,
} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {AnalyticsReportJobPrioritiesEnum} from '../../../store/panels/analytics/report/analyticsPanelReportState';
import {getReportOptionsWithBucketBoundaries, ReportElementIdsEnum} from '../analyticsReportHelpers';

/**
 * Custom hook to update the selected metrics.
 */
export const useAnalyticsUpdateSelectedMetrics = () => {
  const dispatch = useAppDispatch();
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);
  const jobs = useAppSelector(getAnalyticsReportJobs);
  const {hiddenMetrics} = useAppSelector(getAnalyticsMetricsToFetch);
  const reportType = useAppSelector(getAnalyticsReportType);
  const bucketBoundaries = useAppSelector(getAnalyticsBucketBoundaries);
  const hasResolutionMetrics = useAppSelector(getHasAnalyticsResolutionMetrics);

  const updateSelectedMetrics = (metrics: ReadonlyArray<string>, reportElementId: ReportElementIdsEnum) => {
    const payload: AnalyticsSelectedMetricsModel = {
      ...selectedMetrics,
      [reportElementId]: metrics,
    };

    dispatch(updateAnalyticsSelectedMetrics(payload));
  };

  // This method will potentially fetch hidden metrics, we pass the newly selected metric to check if we need to fetch them or not.
  const maybeFetchHiddenMetrics = (newlySelectedMetric: AnalyticsMetricsKeys) => {
    // Do we already have a job containing this metric the user just selected?
    if (jobs.some((job) => job.metrics.includes(newlySelectedMetric))) {
      return;
    } // Then it's all good - it's either already fetched or being fetched.

    // Otherwise it means we need to fetch the hidden metrics.
    const options = getReportOptionsWithBucketBoundaries(
      reportType,
      hiddenMetrics,
      hasResolutionMetrics,
      bucketBoundaries,
    );
    dispatch(fetchAnalyticsMetrics(hiddenMetrics, AnalyticsReportJobPrioritiesEnum.SECONDARY, options));
  };

  return {updateSelectedMetrics, maybeFetchHiddenMetrics};
};
